import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Building2, MapPin, Mail, Phone, Receipt, Landmark, CreditCard,
  Loader2, ArrowRight, ArrowLeft, CheckCircle2, Plus, Zap
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SetupField({ label, id, value, onChange, placeholder, type = "text", icon: Icon, hint, suffix }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${Icon ? "pl-10" : ""} ${suffix ? "pr-10" : ""}`}
          data-testid={`field-${id}`}
        />
        {suffix}
      </div>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function CompanySetupPage() {
  const { isAuthenticated, loading: authLoading, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [gstinValidating, setGstinValidating] = useState(false);
  const [gstinValid, setGstinValid] = useState(null);
  const [form, setForm] = useState({
    company_name: "", gst_number: "", pan_number: "", address: "", state: "",
    email: "", phone: "", default_gst_percent: 18, tax_type: "CGST_SGST",
    hsn_codes: "",
    invoice_prefix: "INV",
    bank_account_name: "", bank_account_number: "", bank_ifsc: "", bank_name: "", upi_id: "",
  });

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // Auto-validate and extract from GSTIN
  const handleGstinChange = async (gstin) => {
    update("gst_number", gstin);
    setGstinValid(null);

    if (gstin.length === 15) {
      setGstinValidating(true);
      try {
        const res = await axios.post(`${API}/validate-gstin`, { gstin });
        if (res.data.valid) {
          setGstinValid(true);
          // Auto-fill PAN and state
          if (res.data.pan) update("pan_number", res.data.pan);
          if (res.data.state) update("state", res.data.state);
          toast.success(`GSTIN valid — ${res.data.state} (${res.data.pan})`);
        } else {
          setGstinValid(false);
          toast.error("Invalid GSTIN format");
        }
      } catch {
        setGstinValid(false);
      } finally {
        setGstinValidating(false);
      }
    }
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }

    // Non-GST confirmation flow
    const trimmedGst = (form.gst_number || "").trim();
    if (!trimmedGst) {
      const confirmed = window.confirm(
        "GST number not provided. Invoices will be generated as Non-GST invoices without GST tax calculations.\n\nContinue without GSTIN?"
      );
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        gst_registered: !!trimmedGst,
        hsn_codes: form.hsn_codes ? form.hsn_codes.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      const res = await axios.post(`${API}/companies`, payload);

      // Set as default company
      await axios.put(`${API}/companies/${res.data.company_id}/set-default`);

      await refreshAuth();
      toast.success("Company created! You're all set.");
      navigate("/create");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save company");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  if (!isAuthenticated) {
    navigate("/auth");
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="company-setup-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <Zap className="w-7 h-7 text-blue-600" />
            </div>
          </motion.div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Set up your business in 60 seconds
          </h1>
          <p className="text-sm text-slate-500 mt-1">We'll auto-fill what we can from your GSTIN</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[{ n: 1, label: "Company" }, { n: 2, label: "Tax & Defaults" }, { n: 3, label: "Bank Details" }].map(s => (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                step >= s.n ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step > s.n ? <CheckCircle2 className="w-4 h-4" /> : s.n}
              </div>
              <span className={`text-sm font-medium hidden sm:block ${step >= s.n ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
              {s.n < 3 && <div className={`flex-1 h-0.5 ${step > s.n ? "bg-blue-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-8"
        >
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Company Information</h2>

              {/* GSTIN first — auto-fills PAN and state */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  GSTIN {gstinValidating && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}
                  {gstinValid === true && <CheckCircle2 className="inline w-4 h-4 text-emerald-500 ml-1" />}
                  {gstinValid === false && <span className="text-red-500 text-xs ml-1">(Invalid)</span>}
                </label>
                <div className="relative">
                  <Receipt className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={form.gst_number}
                    onChange={(e) => handleGstinChange(e.target.value.toUpperCase())}
                    placeholder="29AABCT1234A1Z5"
                    className="pl-10 uppercase font-mono"
                    maxLength={15}
                    data-testid="field-gst_number"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Enter GSTIN to auto-fill PAN and state</p>
              </div>

              <SetupField label="Company Name *" id="company_name" value={form.company_name} onChange={v => update("company_name", v)} placeholder="Your Company Pvt Ltd" icon={Building2} />

              <div className="grid grid-cols-2 gap-4">
                <SetupField label="PAN Number" id="pan_number" value={form.pan_number} onChange={v => update("pan_number", v)} placeholder="AABCT1234A" hint={form.gst_number.length === 15 ? "Auto-filled from GSTIN" : ""} />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">State</label>
                  <Input
                    value={form.state}
                    onChange={(e) => update("state", e.target.value)}
                    placeholder={form.gst_number.length === 15 ? "Auto-filled" : "e.g. Karnataka"}
                    data-testid="field-state"
                  />
                  {form.gst_number.length === 15 && form.state && <p className="text-xs text-emerald-500 mt-1">Auto-filled from GSTIN</p>}
                </div>
              </div>

              <SetupField label="Address" id="address" value={form.address} onChange={v => update("address", v)} placeholder="123 MG Road, Bangalore" icon={MapPin} />

              <div className="grid grid-cols-2 gap-4">
                <SetupField label="Email" id="email" value={form.email} onChange={v => update("email", v)} placeholder="billing@company.com" icon={Mail} type="email" />
                <SetupField label="Phone" id="phone" value={form.phone} onChange={v => update("phone", v)} placeholder="+91 98765 43210" icon={Phone} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Tax & Invoice Defaults</h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Default GST Rate (%)</label>
                  <Select value={String(form.default_gst_percent)} onValueChange={v => update("default_gst_percent", parseFloat(v))}>
                    <SelectTrigger data-testid="field-gst_percent"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0% (Exempt)</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="18">18% (Most common)</SelectItem>
                      <SelectItem value="28">28%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Default Tax Type</label>
                  <Select value={form.tax_type} onValueChange={v => update("tax_type", v)}>
                    <SelectTrigger data-testid="field-tax_type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CGST_SGST">CGST + SGST (Intra-state)</SelectItem>
                      <SelectItem value="IGST">IGST (Inter-state)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <SetupField
                label="Invoice Number Prefix"
                id="invoice_prefix"
                value={form.invoice_prefix}
                onChange={v => update("invoice_prefix", v)}
                placeholder="INV"
                hint="Your invoices will be numbered: INV-0001, INV-0002, etc."
              />

              <SetupField
                label="Common HSN/SAC Codes"
                id="hsn_codes"
                value={form.hsn_codes}
                onChange={v => update("hsn_codes", v)}
                placeholder="998361, 998313"
                hint="Comma-separated. These will appear as suggestions when creating invoices."
              />

              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-sm text-blue-800 font-medium mb-1">Smart GST Detection</p>
                <p className="text-xs text-blue-600">When creating invoices, GST type (CGST+SGST vs IGST) will be auto-detected based on your state and customer state.</p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Bank Details</h2>
              <p className="text-sm text-slate-500">These will appear on your invoices for payment collection.</p>

              <SetupField label="Account Holder Name" id="bank_account_name" value={form.bank_account_name} onChange={v => update("bank_account_name", v)} placeholder="Your Company Pvt Ltd" icon={Landmark} />
              <SetupField label="Account Number" id="bank_account_number" value={form.bank_account_number} onChange={v => update("bank_account_number", v)} placeholder="50100123456789" />
              <div className="grid grid-cols-2 gap-4">
                <SetupField label="IFSC Code" id="bank_ifsc" value={form.bank_ifsc} onChange={v => update("bank_ifsc", v)} placeholder="HDFC0001234" />
                <SetupField label="Bank Name" id="bank_name" value={form.bank_name} onChange={v => update("bank_name", v)} placeholder="HDFC Bank" />
              </div>
              <SetupField label="UPI ID" id="upi_id" value={form.upi_id} onChange={v => update("upi_id", v)} placeholder="company@upi" icon={CreditCard} />
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep(s => s - 1)} data-testid="prev-step-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : <div />}
            {step < 3 ? (
              <Button onClick={() => {
                if (step === 1 && !form.company_name.trim()) { toast.error("Company name is required"); return; }
                setStep(s => s + 1);
              }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="next-step-btn">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-company-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Create Company
              </Button>
            )}
          </div>
        </motion.div>

        {/* Skip option */}
        <p className="text-center text-xs text-slate-400 mt-4">
          You can always update these details later in Settings.
        </p>
      </div>
    </div>
  );
}
